import { isNil } from 'lodash';
import * as THREE from 'three';

/**
 * Get the dimensions of the graph.
 * @param containerWidth - The width of the container.
 * @param containerHeight - The height of the container.
 * @param marginLeft - The left margin.
 * @param marginBottom - The bottom margin.
 * @returns The dimensions of the graph.
 */
export const getGraphDimensions = (
  containerWidth: number,
  containerHeight: number,
  marginLeft: number,
  marginBottom: number,
) => ({
  width: containerWidth - marginLeft,
  height: containerHeight - marginBottom,
});

/**
 * Fit the camera to the data.
 * @param camera - The camera to fit.
 * @param bounds - The bounds of the graph.
 * @param scale - The scale of the graph.
 * @param canvasWidth - The width of the canvas.
 * @param canvasHeight - The height of the canvas.
 * @returns The optimal scale.
 */
export const fitCameraToData = (
  camera: THREE.OrthographicCamera,
  bounds: THREE.Box3,
  scale: THREE.Vector3,
  canvasWidth: number,
  canvasHeight: number,
): void => {
  if (bounds.isEmpty()) {
    return;
  }

  const size = bounds.getSize(new THREE.Vector3());
  const center = bounds.getCenter(new THREE.Vector3());

  const scaleX = canvasWidth / size.x;
  const scaleY = canvasHeight / size.z;
  const optimalScale = Math.min(scaleX, scaleY);

  scale.set(optimalScale, optimalScale, 1);
  camera.position.copy(center);
};

/**
 * Format the tick value for the axes up to 2 decimal places.
 * @param value - The value to format.
 * @returns The formatted value.
 */
export const formatTickValue = (value: number): string => {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(2);
};

/**
 * Update the camera bounds.
 * @param camera - The camera to update.
 * @param graphWidth - The width of the graph.
 * @param graphHeight - The height of the graph.
 * @param scale - The scale of the graph.
 */
export const updateCameraBounds = (
  camera: THREE.OrthographicCamera | null,
  graphWidth: number,
  graphHeight: number,
  scale: THREE.Vector3,
): void => {
  if (isNil(camera)) {
    return;
  }

  camera.left = -graphWidth / 2 / scale.x;
  camera.right = graphWidth / 2 / scale.x;
  camera.top = graphHeight / 2 / scale.y;
  camera.bottom = -graphHeight / 2 / scale.y;
  camera.updateProjectionMatrix();
};
